"use strict";
/**
 * OpenAIRealtimeCtrl – Manages a WebSocket connection to the OpenAI Realtime
 * API.  Receives final Deepgram transcriptions (last 3) and sends them as
 * conversation items so the model can produce coaching / analysis responses.
 *
 * Responses are stored in `$scope.openaiRealtimeResponses` for display in a
 * tab alongside the live transcription pane.
 *
 * Lifecycle:
 *   - `setupOpenAIRealtime()`   – called when Deepgram starts (onCallAnswered)
 *   - `teardownOpenAIRealtime()` – called when Deepgram stops (settingOnEndCallLayout / $destroy)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupOpenAIRealtime = setupOpenAIRealtime;
exports.onOpenAIResponse = onOpenAIResponse;
exports.onAddData = onAddData;
exports.onAddRebuttal = onAddRebuttal;
exports.teardownOpenAIRealtime = teardownOpenAIRealtime;
exports.sendTranscriptions = sendTranscriptions;
// ─── Constants ───────────────────────────────────────────────────────────────
const OPENAI_REALTIME_URL = 'wss://api.openai.com/v1/realtime?model=gpt-realtime';
/** System prompt that instructs the model on how to behave. */
const SYSTEM_INSTRUCTIONS = (`<role>
Your job is to help an agent during a live call, collecting data and assisting actively with rebitals and arguments to close the deal.
Every time you hear a customer objection, you should respond with a concise and relevant rebuttal to help the agent overcome the objection and move the sale forward.
We are selling final expense insurance.
</role>
<conditions>
You can pay monthly, the final price will depend on the customer's age and health conditions, and smoking habits.
</conditions>

<Objections and Rebuttals>
These are some example objections along with the rationale from the customer, buil your rebuttals around this:
- "I need to think about it":       What it really means: I don’t trust you yet, I don’t fully understand it, I’m overwhelmed, I want to avoid confrontation.
- "It's too expensive":             Rebuttal Argument: Offer to share some options that might fit their budget, and emphasize the value and peace of mind that the insurance can provide.
- "I already have insurance":       What it really means:I don’t understand what I have, I have term that may expire,I have small coverage, I want you off the phone.
- "Send me something over email":   What it really means:I want to end this call, I’m not ready to decide, I don’t buy over the phone, Mail = 90% no sale in telesales.

These are just examples, you should come up with your own rebuttals based on the specific objection and the customer's concerns.
Mandatory: When you want to give a rebuttal use the function "add_rebuttal", passing the objection and full rebuttal, and reply just "ok", DO NOT REPEAT THE REBUTTAL.
</Objections and Rebuttals>

<data gathering>
Use "add_data" when you detect any of the following information:
first name, last name, age, date of birth (m-d-yyyy),
Zip Code,
address,
city,
height,
weight,
tobacco use (Yes/No),
email,
benefitiary First Name,
benefitiary last Name,
social security or disability (pick one)
coverage Type (burrial, cremation),
bank type ('Local Bank' ,'Credit Union','Direct Express Card'),
diseases list (array of strings, e.g. ["diabetes", "high blood pressure"]),
life insurance (true/false - if the customer is calling for life insurance or final expense insurance, false if not.),
price aware (true if the agent mention or implies this is not a free service, false if the customer expected free benefits)
.

Mandatory: When you want to add data use the function "add_data", passing the key and value, and reply just "ok", DO NOT REPEAT THE DATA POINT nor engage with the user.
</data gathering>
MANDATORY: Never reply directly, only reply "K" and use a tool when necessary.
`);
/** Tool definitions exposed to the model. */
const TOOLS = [
    {
        type: "function",
        name: "add_data",
        description: "Stores a key-value pair of data collected from the conversation (e.g. customer name, age, health conditions).",
        parameters: {
            type: "object",
            properties: {
                key: { type: "string", description: "The label or field name for the data point." },
                value: { type: "string", description: "The value associated with the key." },
            },
            required: ["key", "value"],
        },
    },
    {
        type: "function",
        name: "add_rebuttal",
        description: "Records a customer objection and the suggested rebuttal for the agent to use.",
        parameters: {
            type: "object",
            properties: {
                objection: { type: "string", description: "The objection raised by the customer." },
                rebuttal: { type: "string", description: "The suggested rebuttal for the agent." },
            },
            required: ["objection", "rebuttal"],
        },
    },
];
// ─── Service-like helper functions ───────────────────────────────────────────
let _ws = null;
let _openaiEphemeralToken = null;
let _responseBuffer = {};
let _emittedResponseIds = new Set();
let _onResponse = null;
let _onAddData = null;
let _onAddRebuttal = null;
let _pendingFunctionCalls = {};
let _handledFunctionCallIds = new Set();
let _responseInProgress = false;
let _pendingTranscriptionBatch = null;
let _pendingTranscriptionsWhileConnecting = null;
/**
 * Fetch a short-lived ephemeral token from `ephemeralTokenUrl`, then open
 * a WebSocket to the OpenAI Realtime API.  Any transcriptions sent while
 * the connection is being established are buffered and flushed once it opens.
 */
function setupOpenAIRealtime(ephemeralTokenUrl) {
    teardownOpenAIRealtime();
    console.log('[OpenAI-RT] Fetching ephemeral token from', ephemeralTokenUrl);
    fetch(ephemeralTokenUrl)
        .then(function (response) {
        if (!response.ok) {
            throw new Error('[OpenAI-RT] Ephemeral token request failed: ' + response.status);
        }
        return response.json();
    })
        .then(function (data) {
        if (!data || !data.ephemeralKey) {
            throw new Error('[OpenAI-RT] Ephemeral token response missing `token` field');
        }
        _openaiEphemeralToken = data.ephemeralKey;
        _openConnection();
    })
        .catch(function (err) {
        console.error('[OpenAI-RT] Failed to obtain ephemeral token', err);
    });
}
/**
 * Register a callback invoked every time a complete response is received.
 */
function onOpenAIResponse(cb) {
    _onResponse = cb;
}
/**
 * Register a callback invoked when the model calls the add_data tool.
 * Receives the key and value extracted from the conversation.
 */
function onAddData(cb) {
    _onAddData = cb;
}
/**
 * Register a callback invoked when the model calls the add_rebuttal tool.
 * Receives the objection and rebuttal strings.
 */
function onAddRebuttal(cb) {
    _onAddRebuttal = cb;
}
/**
 * Internal: open the actual WebSocket using the already-set ephemeral token.
 * Called by `setupOpenAIRealtime` after the token is fetched.
 */
function _openConnection() {
    console.log('[OpenAI-RT] Opening WebSocket to OpenAI Realtime API');
    // Browser WebSocket cannot set custom HTTP headers, so we authenticate
    // via the Sec-WebSocket-Protocol sub-protocol trick supported by OpenAI:
    //   realtime, openai-insecure-api-key.<TOKEN>, openai-beta.realtime-v1
    var ws = new WebSocket(OPENAI_REALTIME_URL, [
        'realtime',
        'openai-insecure-api-key.' + _openaiEphemeralToken,
    ]);
    _ws = ws;
    ws.onopen = function () {
        // If this socket was superseded by teardown or a newer setup, do nothing.
        if (_ws !== ws) {
            return;
        }
        console.log('[OpenAI-RT] Connection open – sending session.update');
        var sessionUpdate = {
            type: 'session.update',
            session: {
                type: 'realtime',
                modalities: ['text'],
                instructions: SYSTEM_INSTRUCTIONS,
                tools: TOOLS,
                tool_choice: 'auto',
            },
        };
        ws.send(JSON.stringify(sessionUpdate));
        // Flush any transcriptions that arrived while the connection was establishing
        if (_pendingTranscriptionsWhileConnecting) {
            var pendingOnOpen = _pendingTranscriptionsWhileConnecting;
            _pendingTranscriptionsWhileConnecting = null;
            _flushTranscriptions(pendingOnOpen);
        }
    };
    ws.onmessage = function (event) {
        if (_ws !== ws) {
            return;
        }
        try {
            var msg = JSON.parse(event.data);
            handleServerEvent(msg);
        }
        catch (e) {
            console.error('[OpenAI-RT] Failed to parse message', e);
        }
    };
    ws.onerror = function (err) {
        console.error('[OpenAI-RT] WebSocket error', err);
    };
    ws.onclose = function (event) {
        console.log('[OpenAI-RT] Connection closed', event.code, event.reason);
        // Only clear the module reference if it still points to this socket.
        if (_ws === ws) {
            _ws = null;
        }
    };
}
/**
 * Close the OpenAI Realtime WebSocket and reset internal state.
 * Safe to call even when no connection is active.
 */
function teardownOpenAIRealtime() {
    if (_ws) {
        try {
            if (_ws.readyState === WebSocket.OPEN || _ws.readyState === WebSocket.CONNECTING) {
                _ws.close(1000, 'teardown');
            }
        }
        catch (e) { }
        _ws = null;
    }
    _responseBuffer = {};
    _emittedResponseIds = new Set();
    _pendingFunctionCalls = {};
    _handledFunctionCallIds = new Set();
    _responseInProgress = false;
    _pendingTranscriptionBatch = null;
    _pendingTranscriptionsWhileConnecting = null;
    console.log('[OpenAI-RT] Torn down');
}
/**
 * Send the last N final transcription entries to the OpenAI Realtime session
 * so the model can produce a coaching response.
 */
function sendTranscriptions(entries) {
    if (!_ws) {
        console.warn('[OpenAI-RT] Cannot send transcriptions – WebSocket not connected');
        return;
    }
    if (_ws.readyState === WebSocket.CONNECTING) {
        // Buffer the latest batch; it will be sent once onopen fires
        _pendingTranscriptionsWhileConnecting = entries;
        console.log('[OpenAI-RT] WebSocket still connecting – buffering transcription batch');
        return;
    }
    if (_ws.readyState !== WebSocket.OPEN) {
        console.warn('[OpenAI-RT] Cannot send transcriptions – WebSocket not open');
        return;
    }
    if (_responseInProgress) {
        // Store the latest batch; it will be flushed once the active response finishes
        _pendingTranscriptionBatch = entries;
        console.log('[OpenAI-RT] Response in progress – queuing transcription batch');
        return;
    }
    _flushTranscriptions(entries);
}
function _flushTranscriptions(entries) {
    if (!_ws || _ws.readyState !== WebSocket.OPEN) {
        return;
    }
    // Build a single user message from the transcript entries
    var combined = entries
        .map(function (e) { return e.speakerLabel + ': ' + e.text; })
        .join('\n');
    var item = {
        type: 'conversation.item.create',
        item: {
            type: 'message',
            role: 'user',
            content: [{ type: 'input_text', text: combined }],
        },
    };
    _ws.send(JSON.stringify(item));
    // Ask the model to respond (modality already set to text in session.update)
    _ws.send(JSON.stringify({ type: 'response.create' }));
    _responseInProgress = true;
    console.log('[OpenAI-RT] Sent ' + entries.length + ' transcriptions, requesting response');
}
// ─── Internal event handler ──────────────────────────────────────────────────
function handleServerEvent(msg) {
    switch (msg.type) {
        case 'session.created':
            console.log('[OpenAI-RT] Session created', msg.session && msg.session.id);
            break;
        case 'session.updated':
            console.log('[OpenAI-RT] Session updated');
            break;
        // ── Streaming text deltas (accumulate for live display if needed) ──
        case 'response.output_text.delta':
            if (msg.response_id && msg.delta) {
                if (!_responseBuffer[msg.response_id]) {
                    _responseBuffer[msg.response_id] = '';
                }
                _responseBuffer[msg.response_id] += msg.delta;
            }
            break;
        // ── Text content part complete ──
        case 'response.output_text.done':
            if (msg.response_id) {
                // prefer the accumulated buffer; fall back to the event's own `text` field
                var doneText = _responseBuffer[msg.response_id] || msg.text || '';
                delete _responseBuffer[msg.response_id];
                if (doneText) {
                    emitResponse(msg.response_id, doneText);
                }
            }
            break;
        // ── Function call arguments streaming ──
        case 'response.function_call_arguments.delta':
            if (msg.call_id) {
                if (!_pendingFunctionCalls[msg.call_id]) {
                    _pendingFunctionCalls[msg.call_id] = { name: msg.name || '', args: '' };
                }
                _pendingFunctionCalls[msg.call_id].args += (msg.delta || '');
            }
            break;
        // ── Function call complete ──
        case 'response.function_call_arguments.done':
            if (msg.call_id) {
                var pending = _pendingFunctionCalls[msg.call_id];
                var fnName = msg.name || (pending && pending.name) || 'unknown';
                var fnArgs = msg.arguments || (pending && pending.args) || '{}';
                delete _pendingFunctionCalls[msg.call_id];
                handleFunctionCall(msg.call_id, fnName, fnArgs);
            }
            break;
        // ── Full response complete – reliable catch-all ──
        case 'response.done': {
            var response = msg.response;
            _responseInProgress = false;
            if (!response)
                break;
            // Clean up any leftover buffer entry
            if (response.id) {
                delete _responseBuffer[response.id];
            }
            var outputs = response.output || [];
            outputs.forEach(function (outputItem) {
                if (outputItem.type === 'message') {
                    // Only emit if response.output_text.done hasn't already emitted it
                    if (_emittedResponseIds.has(response.id)) {
                        _emittedResponseIds.delete(response.id);
                        return;
                    }
                    var parts = outputItem.content || [];
                    parts.forEach(function (part) {
                        if (part.type === 'text' && part.text) {
                            emitResponse(response.id + '_done', part.text);
                        }
                    });
                }
                else if (outputItem.type === 'function_call') {
                    // Handle function calls surfaced in response.done in case
                    // function_call_arguments.done was missed
                    var callId = outputItem.call_id;
                    if (callId && !_pendingFunctionCalls[callId]) {
                        handleFunctionCall(callId, outputItem.name || '', outputItem.arguments || '{}');
                    }
                }
            });
            // Flush any transcription batch that arrived while this response was in flight
            if (_pendingTranscriptionBatch) {
                var batch = _pendingTranscriptionBatch;
                _pendingTranscriptionBatch = null;
                _flushTranscriptions(batch);
            }
            break;
        }
        case 'error':
            console.error('[OpenAI-RT] Server error', msg.error);
            break;
        default:
            break;
    }
}
/**
 * Handle a function call from the model.  We resolve it immediately with
 * a simple acknowledgment so the model can continue producing text.
 */
function handleFunctionCall(callId, name, argsJson) {
    if (_handledFunctionCallIds.has(callId)) {
        return;
    }
    _handledFunctionCallIds.add(callId);
    console.log('[OpenAI-RT] Function call:', name, argsJson);
    try {
        var args = JSON.parse(argsJson);
        var displayText = '';
        switch (name) {
            case 'add_rebuttal':
                displayText = '⚠ Objection: ' + (args.objection || '') +
                    (args.rebuttal ? '\n💡 Rebuttal: ' + args.rebuttal : '');
                if (_onAddRebuttal && args.objection != null && args.rebuttal != null) {
                    _onAddRebuttal(args.objection, args.rebuttal);
                }
                break;
            case 'add_data':
                displayText = '📋 Data captured — ' + (args.key || '') + ': ' + (args.value || '');
                if (_onAddData && args.key != null && args.value != null) {
                    _onAddData(args.key, args.value);
                }
                break;
            default:
                displayText = '🔧 ' + name + ': ' + argsJson;
        }
        emitResponse(callId + '_tool', displayText);
    }
    catch (e) {
        console.error('[OpenAI-RT] Failed to parse function call args', e);
    }
    // Acknowledge the function call so the model can continue
    if (_ws && _ws.readyState === WebSocket.OPEN) {
        _ws.send(JSON.stringify({
            type: 'conversation.item.create',
            item: {
                type: 'function_call_output',
                call_id: callId,
                output: JSON.stringify({ status: 'acknowledged' }),
            },
        }));
    }
}
function emitResponse(id, text) {
    if (!text)
        return;
    if (_emittedResponseIds.has(id))
        return;
    _emittedResponseIds.add(id);
    var entry = {
        id: id,
        text: text,
        timestamp: Date.now(),
    };
    console.log('[OpenAI-RT] Response:', text.substring(0, 120));
    if (_onResponse) {
        _onResponse(entry);
    }
}
